import type { Column, ColumnFiltersState, RowData, SortingState, Table } from "@tanstack/react-table"
import { createContext, type ReactNode, useContext, useLayoutEffect, useMemo } from "react"

import { cn } from "@/lib/utils"

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Typed access to the column value for host renderers. */
    formatValue?: (value: TValue) => ReactNode
    headerTitle?: string
    headerClassName?: string
    cellClassName?: string
    skeleton?: ReactNode
    expandedContent?: (row: TData) => ReactNode
    autoSize?: boolean
  }
}

/** Label for headers / column visibility: `meta.headerTitle`, string `columnDef.header`, or `column.id`. */
export function getColumnHeaderLabel<TData, TValue>(column: Column<TData, TValue>): string {
  const meta = column.columnDef.meta as { headerTitle?: string } | undefined
  if (typeof meta?.headerTitle === "string") return meta.headerTitle
  const defHeader = column.columnDef.header
  if (typeof defHeader === "string") return defHeader
  return String(column.id)
}

export type DataGridApiFetchParams = {
  pageIndex: number
  pageSize: number
  sorting?: SortingState
  filters?: ColumnFiltersState
  searchQuery?: string
}

export type DataGridApiResponse<T> = {
  data: T[]
  empty: boolean
  pagination: {
    total: number
    page: number
  }
}

export interface DataGridContextProps<TData extends object> {
  props: DataGridProps<TData>
  table: Table<TData>
  recordCount: number
  isLoading: boolean
}

export type DataGridRequestParams = {
  pageIndex: number
  pageSize: number
  sorting?: SortingState
  columnFilters?: ColumnFiltersState
}

export interface DataGridProps<TData extends object> {
  className?: string
  table?: Table<TData>
  recordCount: number
  children?: ReactNode
  onRowClick?: (row: TData) => void
  isLoading?: boolean
  loadingMode?: "skeleton" | "spinner"
  loadingMessage?: ReactNode | string
  fetchingMoreMessage?: ReactNode | string
  allRowsLoadedMessage?: ReactNode | string
  emptyMessage?: ReactNode | string
  tableLayout?: {
    dense?: boolean
    cellBorder?: boolean
    rowBorder?: boolean
    rowRounded?: boolean
    stripped?: boolean
    headerBackground?: boolean
    footerBackground?: boolean
    headerBorder?: boolean
    headerSticky?: boolean
    width?: "auto" | "fixed"
    columnsVisibility?: boolean
    columnsResizable?: boolean
    columnsResizeMode?: "onChange" | "onEnd"
    columnsPinnable?: boolean
    columnsMovable?: boolean
    columnsDraggable?: boolean
    rowsDraggable?: boolean
    rowsPinnable?: boolean
  }
  tableClassNames?: {
    base?: string
    header?: string
    headerRow?: string
    headerSticky?: string
    body?: string
    bodyRow?: string
    footer?: string
    edgeCell?: string
  }
}

// TanStack tables are invariant in row type; the provider owns this type-erased context.
/* eslint-disable @typescript-eslint/no-explicit-any -- The provider owns the erased TanStack row type. */
// biome-ignore lint/suspicious/noExplicitAny: TanStack tables are invariant; the provider erases only the row type.
const DataGridContext = createContext<DataGridContextProps<any> | undefined>(undefined)
/* eslint-enable @typescript-eslint/no-explicit-any */

function useDataGrid() {
  const context = useContext(DataGridContext)
  if (!context) {
    throw new Error("useDataGrid must be used within a DataGridProvider")
  }
  return context
}

function DataGridProvider<TData extends object>({
  children,
  table,
  ...props
}: DataGridProps<TData> & { table: Table<TData> }) {
  const resolvedColumnsResizeMode = props.tableLayout?.columnsResizeMode ?? "onEnd"

  // Keep resize mode aligned through TanStack's public setter instead of mutating the
  // consumer-owned table options during render.
  useLayoutEffect(() => {
    if (!props.tableLayout?.columnsResizable) return
    table.setOptions((previous) => ({
      ...previous,
      columnResizeMode: resolvedColumnsResizeMode,
    }))
  }, [props.tableLayout?.columnsResizable, resolvedColumnsResizeMode, table])

  // The provider publishes the host table together with its current presentation props.
  const value = useMemo(
    () => ({
      props,
      table,
      recordCount: props.recordCount,
      isLoading: props.isLoading || false,
    }),
    [props, table],
  )

  return <DataGridContext.Provider value={value}>{children}</DataGridContext.Provider>
}

function DataGrid<TData extends object>({ children, table, ...props }: DataGridProps<TData>) {
  const defaultProps: Partial<DataGridProps<TData>> = {
    loadingMode: "skeleton",
    tableLayout: {
      dense: false,
      cellBorder: false,
      rowBorder: true,
      rowRounded: false,
      stripped: false,
      headerSticky: false,
      headerBackground: false,
      footerBackground: false,
      headerBorder: true,
      width: "fixed",
      columnsVisibility: false,
      columnsResizable: false,
      columnsResizeMode: "onEnd",
      columnsPinnable: false,
      columnsMovable: false,
      columnsDraggable: false,
      rowsDraggable: false,
      rowsPinnable: false,
    },
    tableClassNames: {
      base: "",
      header: "",
      headerRow: "",
      headerSticky: "sticky top-0 z-15 bg-background/90 backdrop-blur-xs",
      body: "",
      bodyRow: "",
      footer: "",
      edgeCell: "",
    },
  }

  const mergedProps: DataGridProps<TData> = {
    ...defaultProps,
    ...props,
    tableLayout: {
      ...defaultProps.tableLayout,
      ...(props.tableLayout || {}),
    },
    tableClassNames: {
      ...defaultProps.tableClassNames,
      ...(props.tableClassNames || {}),
    },
  }

  // Ensure table is provided
  if (!table) {
    throw new Error('DataGrid requires a "table" prop')
  }

  return (
    <DataGridProvider table={table} {...mergedProps}>
      {children}
    </DataGridProvider>
  )
}

function DataGridContainer({
  children,
  className,
  // `border` isn't consumed by this vendor render (rendering doesn't branch on it) — kept
  // in the prop signature so external callers passing `border={false}` still type-check
  // against the upstream ReUI API, renamed with the TS unused-parameter underscore
  // convention rather than dropped, to avoid diverging from the installed registry file.
  border = true,
}: {
  children: ReactNode
  className?: string
  border?: boolean
}) {
  void border

  return (
    <div data-slot="data-grid" className={cn("w-full overflow-hidden", className)}>
      {children}
    </div>
  )
}

export { DataGrid, DataGridContainer, DataGridProvider, useDataGrid }
